import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  DogByIDModal,
  EditDogModal,
  InvoiceModal,
  ThemeButton,
  ThemeSelect,
  ThemeTable,
  UpdateAppointmentTimeModal,
  UpdateInvoiceModal,
  UserDetailModal,
  CreateAppointmentModal,
  AppointmentHistoryDrawer,
  AppointmentStatusSelect,
  UpdateAppointment,
} from '../../components/components';
import {
  useGetAppointmentsQuery,
  useUpdateAppointmentMutation,
  useDeleteAppointmentMutation,
} from '../../services/appointments';
import { useLazyGetDogByIDQuery } from '../../services/dogsApi';
import { edit_icon, users_white_icon } from '../../assets/icons/icons';
import dayjs from 'dayjs';
import { message, Modal, Dropdown, Button } from 'antd';
import { Tabs, Checkbox, Switch } from 'antd';
import { useGetServicesQuery } from '../../services/dogServiceApi';
import ThemeDropdown from '../../components/themeDropdown/themeDropdown';
import { useLazyGetUserByIdQuery } from '../../services/users';
import { SearchOutlined, CheckOutlined, MoreOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import {
  BOARDING_SERVICE_ID,
  APPOINTMENT_STATUS,
  PAYMENT_METHODS,
} from '../../constants/constants';
import RefreshIndicator from '../../components/refreshIndicator/refreshIndicator';
import { usePolling } from '../../hooks/usePolling';
import { useAppointmentStatusChange } from '../../hooks/useAppointmentStatusChange';
import {
  formatToEastern,
  formatTimeET,
  formatDateET,
  sortDogsByName,
} from '../../helpers/helpers';

// CSS styles for new appointment highlighting
const newAppointmentStyles = `
  @keyframes slideInGlow {
    0% {
      background-color: rgba(34, 197, 94, 0.4);
      transform: scale(1.02);
      box-shadow: 0 0 15px rgba(34, 197, 94, 0.5);
    }
    50% {
      background-color: rgba(34, 197, 94, 0.2);
      transform: scale(1.01);
      box-shadow: 0 0 10px rgba(34, 197, 94, 0.3);
    }
    100% {
      background-color: rgba(34, 197, 94, 0.1);
      transform: scale(1);
      box-shadow: 0 0 5px rgba(34, 197, 94, 0.2);
    }
  }
  .new-appointment-row {
    animation: slideInGlow 2s ease-out;
    background-color: rgba(34, 197, 94, 0.1) !important;
    border-left: 4px solid #22c55e !important;
    transition: all 0.3s ease;
  }
  .new-appointment-row:hover {
    background-color: rgba(34, 197, 94, 0.15) !important;
  }
  .paid-checkbox .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #22c55e !important;
    border-color: #22c55e !important;
  }
  .paid-checkbox .ant-checkbox-checked .ant-checkbox-inner:after {
    border-color: #ffffff !important;
  }
  .paid-checkbox .ant-checkbox:hover .ant-checkbox-inner {
    border-color: #22c55e !important;
  }
`;

// Inject styles into the document head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.innerText = newAppointmentStyles;
  document.head.appendChild(styleSheet);
}

const Appointments = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [resultPerPage, setResultPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [key, setKey] = useState('all');
  const [isOpenUserModal, setIsOpenUserModal] = useState(false);
  const [isOpenUpdateTimeModal, setIsOpenUpdateTimeModal] = useState(false);
  const [historyAppointmentId, setHistoryAppointmentId] = useState(null);
  const [appointmentTime, setAppointmentTime] = useState(null);
  const [isOpenUpdateInvoiceModal, setIsOpenUpdateInvoiceModal] =
    useState(false);
  const [isOpenInvoice, setIsOpenInvoice] = useState(false);
  const [editId, setEditId] = useState('');
  const [isOpenNotesModal, setIsOpenNotesModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [dogData, setDogData] = useState({});
  const [isEditDogModalOpen, setIsEditDogModalOpen] = useState(false);
  const [userData, setUserData] = useState({});
  const [serviceType, setServiceType] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [unpaidOnly, setUnpaidOnly] = useState(false);
  const unpaidActive = key === 'past' && unpaidOnly;
  const [newAppointmentIds, setNewAppointmentIds] = useState(new Set());
  const previousAppointmentsRef = useRef(new Set());
  const isInitialLoadRef = useRef(true);

  const [invoiceData, setInvoiceData] = useState({});
  const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] =
    useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [pendingAppointmentData, setPendingAppointmentData] = useState(null);
  const [pendingIsChecking, setPendingIsChecking] = useState(false);
  const [isCreateAppointmentModalOpen, setIsCreateAppointmentModalOpen] =
    useState(false);
  const { data: services } = useGetServicesQuery();

  const {
    data,
    currentData,
    refetch,
    isFetching: fetchingAppointments,
  } = useGetAppointmentsQuery({
    currentPage,
    resultPerPage,
    status: key !== 'all' ? key : undefined,
    service:
      serviceType && serviceType !== 'All'
        ? services?.data?.find(data => data?.title === serviceType)._id
        : undefined,
    search: search || undefined,
    unpaidOnly: unpaidActive || undefined,
  });
  // currentData is scoped to the CURRENT args; `data` holds the last result for
  // ANY args, so it stays populated across a tab / page / service / search
  // change and the old `!data` check only ever fired on the very first load —
  // every other transition showed the previous page's rows with live controls
  // until the request landed. The 15s poll refetches the same args with data in
  // hand, so it trips isStale, not isBlocking.
  const isBlocking = fetchingAppointments && !currentData;
  const isStale = fetchingAppointments && !!currentData;

  const [updateAppointment, { isLoading }] = useUpdateAppointmentMutation();
  const [deleteAppointment] = useDeleteAppointmentMutation();
  const [getDogByID, { isLoading: fetchingdog }] = useLazyGetDogByIDQuery();
  const [getUserById] = useLazyGetUserByIdQuery();

  // Pause auto-refresh when search is active
  const isSearchActive = search.length > 0;
  const { manualRefresh } = usePolling(refetch, 15000, isSearchActive);

  const { handleStatusChange, updatingStatusIds } = useAppointmentStatusChange({
    // zIndex keeps the confirm dialog above the appointment detail modal
    zIndex: 2000,
    onSuccess: (newStatus, id) => {
      refetch();
      // The detail modal renders off a snapshot taken when it was opened, so
      // refetch() alone would leave it showing the previous status.
      setInvoiceData(prev =>
        prev?._id === id ? { ...prev, status: newStatus } : prev
      );
    },
  });

  // Debounce search input - wait 400ms after user stops typing.
  // Skip 1–2 character queries: the backend regex-matches across users + dogs
  // after $lookup, and short queries match nearly everything (causes timeouts).
  useEffect(() => {
    const timer = setTimeout(() => {
      const q = searchInput.trim();
      if (q.length === 0 || q.length >= 3) {
        setSearch(q);
        if (q) setCurrentPage(1);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setTotal(data?.data?.total || 0);
  }, [data]);

  // Marking the only row on the last unpaid page as paid removes that page.
  useEffect(() => {
    const latestTotal = currentData?.data?.total;
    if (latestTotal === undefined) return;
    const lastPage = Math.max(1, Math.ceil(latestTotal / resultPerPage));
    if (currentPage > lastPage) setCurrentPage(lastPage);
  }, [currentData, currentPage, resultPerPage]);

  // Track new appointments for highlighting
  useEffect(() => {
    if (data?.data?.appointments) {
      const currentIds = new Set(data.data.appointments.map(apt => apt._id));
      const previousIds = previousAppointmentsRef.current;

      // Skip highlighting on initial load
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
        previousAppointmentsRef.current = currentIds;
        return;
      }

      // Find new appointments (exist in current but not in previous)
      const newIds = new Set();
      currentIds.forEach(id => {
        if (!previousIds.has(id)) {
          newIds.add(id);
        }
      });

      if (newIds.size > 0) {
        setNewAppointmentIds(newIds);
        // Remove highlighting after 5 seconds
        setTimeout(() => {
          setNewAppointmentIds(new Set());
        }, 5000);
      }

      // Update previous appointments ref
      previousAppointmentsRef.current = currentIds;
    }
  }, [data?.data?.appointments]);

  const onOpenModal = async id => {
    setIsOpen(true);
    const response = await getDogByID(id);
    setDogData(response?.data?.data);
  };

  const onOpenUserModal = async data => {
    const userInfo = await getUserById({ id: data._id });
    setUserData(userInfo?.data?.data);
    setIsOpenUserModal(true);
  };

  const onClose = () => {
    setIsOpen(false);
    setDogData({});
  };

  const handleEditDog = () => {
    setIsOpen(false);
    setIsEditDogModalOpen(true);
  };

  const handleCloseEditDogModal = () => {
    setIsEditDogModalOpen(false);
    setDogData({});
  };

  const onCloseUserModal = () => {
    setIsOpenUserModal(false);
    setUserData({});
  };

  const onUpdateInvoice = async inv => {
    let body = {
      appointment: {
        ...inv,
      },
    };
    const response = await updateAppointment({
      id: invoiceData?._id,
      body,
    });
    if (!response?.data?.error) {
      refetch();
    } else {
      message.error(response?.data?.msg || 'Something went wrong!');
    }
  };

  const onUpdateTime = async inv => {
    let body = {
      appointment: {
        ...inv,
      },
    };
    const response = await updateAppointment({
      id: editId,
      body,
    });
    if (!response?.data?.error) {
      message.success('Appointment Time Updated Successfully');
      refetch();
      // Keep the modal open so the admin can choose to send an updated invoice.
    } else {
      message.error(response?.data?.msg || 'Something went wrong!');
    }
    return response;
  };

  const onUpdateNotes = async () => {
    // Free-text notes field. No format validation — empty clears the value.
    const trimmed = (notes ?? '').toString().trim();
    const response = await updateAppointment({
      id: editId,
      body: { appointment: { notes: trimmed === '' ? null : trimmed } },
    });
    if (!response?.data?.error) {
      message.success('Notes Updated Successfully');
      setNotes('');
      setIsOpenNotesModal(false);
      refetch();
    } else {
      message.error(response?.data?.msg || 'Something went wrong!');
    }
  };

  const onChangePaidPrice = async (e, appointmentData) => {
    const isChecking = e.target.checked;
    const appointmentId = e.target.id;

    // If unchecking, proceed directly with confirmation
    if (!isChecking) {
      const customerName =
        appointmentData?.user?.username || 'Unknown Customer';
      const startDate = appointmentData?.appointment_start_time
        ? formatDateET(appointmentData.appointment_start_time, 'DD-MMM-YYYY')
        : 'N/A';
      const endDate = appointmentData?.appointment_end_time
        ? formatDateET(appointmentData.appointment_end_time, 'DD-MMM-YYYY')
        : 'N/A';

      const dateRange =
        startDate === endDate ? startDate : `${startDate} to ${endDate}`;

      Modal.confirm({
        title: 'Confirm Payment Status Change',
        content: (
          <div>
            <p>Are you sure you want to mark this invoice as unpaid?</p>
            <div
              style={{
                marginTop: '12px',
                padding: '8px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
              }}
            >
              <p>
                <strong>Customer:</strong> {customerName}
              </p>
              <p>
                <strong>Service:</strong>{' '}
                {appointmentData?.service?.title || 'N/A'}
              </p>
              <p>
                <strong>Date(s):</strong> {dateRange}
              </p>
              <p>
                <strong>Amount:</strong> $
                {(
                  appointmentData?.netTotal ||
                  appointmentData?.priceAtAppoTime ||
                  0
                ).toFixed(2)}
              </p>
            </div>
          </div>
        ),
        okText: 'Yes',
        cancelText: 'No',
        okButtonProps: {
          style: {
            backgroundColor: '#10b981',
            borderColor: '#10b981',
            color: '#ffffff',
          },
        },
        cancelButtonProps: {
          style: {
            backgroundColor: '#ffffff',
            borderColor: '#d1d5db',
            color: '#374151',
          },
        },
        onOk: async () => {
          await updateAppointment({
            id: appointmentId,
            body: {
              appointment: { isPaid: false, paymentMethod: null },
            },
          });
          refetch();
          message.success('Invoice marked as unpaid successfully');
        },
        onCancel: () => {
          // Force re-render to revert checkbox state
          refetch();
        },
      });
      return;
    }

    // If checking, show payment method selection modal first
    setPendingAppointmentData(appointmentData);
    setPendingIsChecking(true);
    setSelectedPaymentMethod(null);
    setIsPaymentMethodModalOpen(true);
  };

  const handlePaymentMethodConfirm = async () => {
    if (!selectedPaymentMethod) {
      message.error('Please select a payment method');
      return;
    }

    const appointmentId = pendingAppointmentData?._id;
    const customerName =
      pendingAppointmentData?.user?.username || 'Unknown Customer';
    const startDate = pendingAppointmentData?.appointment_start_time
      ? formatDateET(
          pendingAppointmentData.appointment_start_time,
          'DD-MMM-YYYY'
        )
      : 'N/A';
    const endDate = pendingAppointmentData?.appointment_end_time
      ? formatDateET(pendingAppointmentData.appointment_end_time, 'DD-MMM-YYYY')
      : 'N/A';

    const dateRange =
      startDate === endDate ? startDate : `${startDate} to ${endDate}`;

    setIsPaymentMethodModalOpen(false);

    Modal.confirm({
      title: 'Confirm Payment Status Change',
      content: (
        <div>
          <p>Are you sure you want to mark this invoice as paid?</p>
          <div
            style={{
              marginTop: '12px',
              padding: '8px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
            }}
          >
            <p>
              <strong>Customer:</strong> {customerName}
            </p>
            <p>
              <strong>Service:</strong>{' '}
              {pendingAppointmentData?.service?.title || 'N/A'}
            </p>
            <p>
              <strong>Date(s):</strong> {dateRange}
            </p>
            <p>
              <strong>Amount:</strong> $
              {(
                pendingAppointmentData?.netTotal ||
                pendingAppointmentData?.priceAtAppoTime ||
                0
              ).toFixed(2)}
            </p>
            <p>
              <strong>Payment Method:</strong> {selectedPaymentMethod}
            </p>
          </div>
        </div>
      ),
      okText: 'Yes',
      cancelText: 'No',
      okButtonProps: {
        style: {
          backgroundColor: '#10b981',
          borderColor: '#10b981',
          color: '#ffffff',
        },
      },
      cancelButtonProps: {
        style: {
          backgroundColor: '#ffffff',
          borderColor: '#d1d5db',
          color: '#374151',
        },
      },
      onOk: async () => {
        await updateAppointment({
          id: appointmentId,
          body: {
            appointment: { isPaid: true, paymentMethod: selectedPaymentMethod },
          },
        });
        refetch();
        message.success('Invoice marked as paid successfully');
        setPendingAppointmentData(null);
        setSelectedPaymentMethod(null);
        setPendingIsChecking(false);
      },
      onCancel: () => {
        // Force re-render to revert checkbox state
        refetch();
        setPendingAppointmentData(null);
        setSelectedPaymentMethod(null);
        setPendingIsChecking(false);
      },
    });
  };

  const handleEditPaymentMethod = appointmentData => {
    setPendingAppointmentData(appointmentData);
    setPendingIsChecking(false);
    setSelectedPaymentMethod(appointmentData?.paymentMethod || null);
    setIsPaymentMethodModalOpen(true);
  };

  const handleUpdatePaymentMethod = async () => {
    if (!selectedPaymentMethod) {
      message.error('Please select a payment method');
      return;
    }

    const appointmentId = pendingAppointmentData?._id;

    const response = await updateAppointment({
      id: appointmentId,
      body: {
        appointment: { paymentMethod: selectedPaymentMethod },
      },
    });

    if (!response?.data?.error) {
      refetch();
      message.success('Payment method updated successfully');
      setIsPaymentMethodModalOpen(false);
      setPendingAppointmentData(null);
      setSelectedPaymentMethod(null);
      setPendingIsChecking(false);
    } else {
      message.error(response?.data?.msg || 'Something went wrong!');
    }
  };

  const columns = [
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      render: text => (
        <p
          className="text-center font-medium cursor-pointer hover:text-primary transition-colors"
          onClick={() => onOpenUserModal(text)}
        >
          {text?.full_name}
        </p>
      ),
    },
    {
      title: 'Service',
      dataIndex: 'service',
      key: 'service',
      render: (text, data) => {
        const isBoardingService = text?._id === BOARDING_SERVICE_ID;
        return (
          <div className="flex flex-col items-center">
            <p className="text-center font-medium">{text?.title}</p>
            <span
              className={`text-xs px-2 py-1 rounded-full ${
                isBoardingService
                  ? 'bg-purple-100 text-purple-800'
                  : 'bg-blue-100 text-blue-800'
              }`}
            >
              {isBoardingService ? '🌙 Overnight' : '☀️ Day-by-Day'}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Dogs',
      dataIndex: 'dogs_details',
      key: 'dogs_details',
      width: 120,
      render: text => {
        // Helper function to calculate vaccination status for a dog
        const getDogVaccinationStatus = dog => {
          if (
            !dog?.medical_attachments ||
            dog.medical_attachments.length === 0
          ) {
            return 'none';
          }

          let hasExpired = false;
          let hasExpiringSoon = false;

          dog.medical_attachments.forEach(attachment => {
            if (!attachment.vaccination_date) {
              hasExpired = true;
              return;
            }

            // Use dayjs for consistent date comparison without locale interference
            const today = dayjs().startOf('day');
            const expiresAt = dayjs(attachment.vaccination_date).startOf('day');

            if (!expiresAt.isValid()) {
              hasExpired = true;
              return;
            }

            const daysUntilExpiration = expiresAt.diff(today, 'day');

            if (daysUntilExpiration < 0) {
              hasExpired = true;
            } else if (daysUntilExpiration <= 30) {
              hasExpiringSoon = true;
            }
          });

          if (hasExpired) return 'expired';
          if (hasExpiringSoon) return 'expiring';
          return 'current';
        };

        const dogsInOrder = sortDogsByName(text);

        return (
          <div className="flex items-center justify-center gap-1 min-w-0">
            {dogsInOrder.length > 0 &&
              dogsInOrder.map((dog, index) => {
                const vacStatus = getDogVaccinationStatus(dog);
                return (
                  <div
                    key={dog?._id || index}
                    className="flex-shrink-0 relative"
                  >
                    <img
                      className="cursor-pointer border-4 border-transparent hover:border-primary rounded-full object-cover w-10 h-10"
                      src={dog?.avatar}
                      onClick={() => onOpenModal(dog?._id)}
                      alt={dog?.dog_name || `Dog ${index + 1}`}
                    />
                    {vacStatus === 'expired' && (
                      <div
                        className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white"
                        title="Expired vaccinations"
                      />
                    )}
                    {vacStatus === 'expiring' && (
                      <div
                        className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full border-2 border-white"
                        title="Vaccinations expiring soon"
                      />
                    )}
                  </div>
                );
              })}
          </div>
        );
      },
    },
    {
      title: 'Appointment Time',
      dataIndex: 'appointment_start_time',
      key: 'appointment_start_time',
      render: (text, data) => {
        const isBoardingService = data?.service?._id === BOARDING_SERVICE_ID;

        return (
          <div className="flex justify-center items-center">
            <div className="flex flex-col justify-center items-center">
              {isBoardingService ? (
                <>
                  <div className="text-center">
                    <p className="text-xs text-gray-600 mb-1">Drop Off:</p>
                    <p className="font-medium">
                      {data?.appointment_start_time
                        ? `${formatToEastern(data?.appointment_start_time, 'DD-MMM-YYYY h:mm A')} ET`
                        : '---'}
                    </p>
                  </div>
                  <div className="text-center mt-2">
                    <p className="text-xs text-gray-600 mb-1">Pickup:</p>
                    <p className="font-medium">
                      {data?.appointment_end_time
                        ? `${formatToEastern(data?.appointment_end_time, 'DD-MMM-YYYY h:mm A')} ET`
                        : '---'}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center">
                    <p className="text-xs text-gray-600 mb-1">Daily In:</p>
                    <p className="font-medium">
                      {data?.appointment_start_time
                        ? formatTimeET(data?.appointment_start_time)
                        : '---'}
                    </p>
                  </div>
                  <div className="text-center mt-2">
                    <p className="text-xs text-gray-600 mb-1">Daily Out:</p>
                    <p className="font-medium">
                      {data?.appointment_end_time
                        ? formatTimeET(data?.appointment_end_time)
                        : '---'}
                    </p>
                  </div>
                  <div className="text-center mt-2">
                    <p className="text-xs text-gray-600 mb-1">Dates:</p>
                    <div className="flex flex-wrap gap-1 justify-center max-w-[200px] mx-auto">
                      {data?.appointment_dates &&
                      data.appointment_dates.length > 0 ? (
                        data.appointment_dates.map((date, index) => (
                          <span
                            key={index}
                            className="inline-block px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium"
                          >
                            {formatDateET(date, 'DD-MMM')}
                          </span>
                        ))
                      ) : (
                        <p className="font-medium text-sm">---</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text, data) => {
        return (
          <div className="flex flex-col items-center justify-center text-center">
            {data?.createdAt ? (
              <>
                <p className="text-xs text-gray-700 font-medium">
                  {formatDateET(data.createdAt, 'DD-MMM-YYYY')}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatTimeET(data.createdAt)}
                </p>
              </>
            ) : (
              <span className="text-gray-300">-</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Invoiced',
      dataIndex: 'invoice_sent_at',
      key: 'invoice_sent_at',
      render: (text, data) => {
        return (
          <div className="flex flex-col items-center justify-center text-center">
            {data?.invoice_sent_at ? (
              <>
                <CheckOutlined style={{ color: '#10b981', fontSize: '18px' }} />
                <p className="text-xs text-gray-500 mt-1">
                  {formatDateET(data.invoice_sent_at)}
                </p>
                <p className="text-xs text-gray-400">
                  {formatTimeET(data.invoice_sent_at)}
                </p>
              </>
            ) : (
              <span className="text-gray-300">-</span>
            )}
            {data?.invoice_dirty && (
              <span className="mt-1 inline-block px-2 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-800 rounded-full">
                Out of date
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Paid Price',
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      render: (text, data) => {
        return (
          <div className="flex flex-col items-center justify-center text-center font-medium gap-2">
            <div className="flex items-center justify-center">
              <Checkbox
                id={data._id}
                checked={data?.isPaid}
                value={data?.isPaid}
                onChange={e => onChangePaidPrice(e, data)}
                className="paid-checkbox"
              ></Checkbox>
            </div>
            {data?.isPaid && data?.paymentMethod && (
              <div className="flex items-center gap-1.5 justify-center">
                <span className="text-sm font-medium text-gray-700 px-2 py-1 bg-gray-100 rounded">
                  {data.paymentMethod}
                </span>
                <img
                  src={edit_icon}
                  alt="edit"
                  className="w-3.5 h-3.5 cursor-pointer hover:opacity-70 transition-opacity"
                  onClick={() => handleEditPaymentMethod(data)}
                  title="Edit payment method"
                />
              </div>
            )}
            {data?.isPaid && !data?.paymentMethod && (
              <span className="text-xs text-gray-400 italic">
                No method set
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      render: (text, data) => (
        <div className="flex items-center justify-center text-center font-medium">
          <span>{text ? text : 'add'}</span>
          <img
            src={edit_icon}
            alt="edit"
            className="w-4 h-4 ml-2 cursor-pointer"
            onClick={() => {
              setEditId(data?._id);
              setNotes(text);
              setIsOpenNotesModal(true);
            }}
          />
        </div>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'netTotal',
      key: 'netTotal',
      render: (text, data) => {
        // Use backend-computed netTotal (single source of truth)
        const grandTotal =
          data?.netTotal ||
          data?.priceBreakdown?.total ||
          data?.priceAtAppoTime ||
          0;

        return (
          <div className="flex items-center justify-center text-center font-bold">
            {grandTotal ? `$${grandTotal.toFixed(2)}` : '-'}
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, data) => (
        <div className="flex justify-center">
          <AppointmentStatusSelect
            status={data?.status}
            disabled={updatingStatusIds.has(data?._id)}
            loading={updatingStatusIds.has(data?._id)}
            onChange={e => handleStatusChange(e, data)}
          />
        </div>
      ),
    },
    {
      title: '',
      dataIndex: 'actions',
      key: 'actions',
      render: (text, data) => {
        const canModify =
          data?.status !== APPOINTMENT_STATUS.CANCELLED &&
          data?.status !== APPOINTMENT_STATUS.CHECKED_OUT;

        // Permanent delete is restricted to non-active appointments, mirroring
        // the backend status guard (pending / rejected / cancelled only).
        const canDelete = [
          APPOINTMENT_STATUS.PENDING,
          APPOINTMENT_STATUS.REJECTED,
          APPOINTMENT_STATUS.CANCELLED,
        ].includes(data?.status);

        const menuItems = [
          { key: 'view-appointment', label: 'View Appointment' },
          ...(canModify
            ? [{ key: 'modify-appointment', label: 'Modify Appointment' }]
            : []),
          { key: 'view-log', label: 'View Log' },
          ...(canDelete
            ? [
                {
                  key: 'delete-appointment',
                  label: 'Delete Appointment',
                  danger: true,
                },
              ]
            : []),
        ];

        const confirmDeleteAppointment = () => {
          Modal.confirm({
            title: 'Delete Appointment',
            content: (
              <div>
                <p>
                  This permanently deletes the appointment. This action cannot
                  be undone.
                </p>
                <div
                  style={{
                    marginTop: '12px',
                    padding: '8px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                  }}
                >
                  <p>
                    <strong>Customer:</strong>{' '}
                    {data?.user?.username || 'Unknown Customer'}
                  </p>
                  <p>
                    <strong>Service:</strong> {data?.service?.title || 'N/A'}
                  </p>
                  <p>
                    <strong>Status:</strong> {data?.status}
                  </p>
                </div>
              </div>
            ),
            okText: 'Delete',
            cancelText: 'Cancel',
            okButtonProps: {
              danger: true,
            },
            onOk: async () => {
              try {
                const res = await deleteAppointment(data._id);
                if (!res?.data?.error) {
                  message.success('Appointment deleted successfully');
                  refetch();
                } else {
                  message.error(
                    res?.data?.msg || 'Failed to delete appointment'
                  );
                }
              } catch (err) {
                message.error('Failed to delete appointment');
              }
            },
          });
        };

        const onMenuClick = ({ key }) => {
          if (key === 'view-appointment') {
            setInvoiceData({ ...data });
            setIsOpenUpdateInvoiceModal(true);
          } else if (key === 'modify-appointment') {
            setEditId(data._id);
            setAppointmentTime(data);
            setIsOpenUpdateTimeModal(true);
          } else if (key === 'view-log') {
            setHistoryAppointmentId(data._id);
          } else if (key === 'delete-appointment') {
            confirmDeleteAppointment();
          }
        };

        return (
          <div className="flex justify-center">
            <Dropdown
              menu={{ items: menuItems, onClick: onMenuClick }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button
                icon={<MoreOutlined />}
                style={{
                  borderRadius: 999,
                  borderColor: '#d1d5db',
                  background: '#fff',
                  color: '#111827',
                }}
              />
            </Dropdown>
          </div>
        );
      },
    },
    // {
    //   title: "",
    //   dataIndex: "_id",
    //   key: "_id",
    //   render: (text) => (
    //     <div
    //       className="w-10 h-10 rounded-full text-white text-2xl bg-primary flex items-center justify-center cursor-pointer"
    //       onClick={() => setIsOpenInvoice(true)}
    //     >
    //       <LiaFileInvoiceSolid />
    //     </div>
    //   ),
    // },
  ];

  const onChange = key => {
    setKey(key);
    setCurrentPage(1);
  };

  const getRowProps = record => {
    return {
      className: newAppointmentIds.has(record._id) ? 'new-appointment-row' : '',
    };
  };

  const items = [
    {
      key: 'all',
      label: 'All',
      children: (
        <ThemeTable
          columns={columns}
          data={data?.data?.appointments || []}
          pagination={{ total, setCurrentPage, setResultPerPage }}
          onRow={getRowProps}
          scroll={{
            x: 1200,
          }}
        />
      ),
    },
    {
      key: 'ongoing',
      label: 'Ongoing',
      children: (
        <ThemeTable
          columns={columns}
          data={data?.data?.appointments || []}
          pagination={{ total, setCurrentPage, setResultPerPage }}
          onRow={getRowProps}
          scroll={{
            x: 1200,
          }}
        />
      ),
    },
    {
      key: 'upcoming',
      label: 'Upcoming',
      children: (
        <ThemeTable
          columns={columns}
          data={data?.data?.appointments || []}
          pagination={{ total, setCurrentPage, setResultPerPage }}
          onRow={getRowProps}
          scroll={{
            x: 1200,
          }}
        />
      ),
    },
    {
      key: 'pending',
      label: 'Pending',
      children: (
        <ThemeTable
          columns={columns}
          data={data?.data?.appointments || []}
          pagination={{ total, setCurrentPage, setResultPerPage }}
          onRow={getRowProps}
          scroll={{
            x: 1200,
          }}
        />
      ),
    },
    {
      key: 'past',
      label: 'Past',
      children: (
        <ThemeTable
          columns={columns}
          data={data?.data?.appointments || []}
          pagination={{ total, setCurrentPage, setResultPerPage }}
          onRow={getRowProps}
          scroll={{
            x: 1200,
          }}
        />
      ),
    },
    {
      key: 'archived',
      label: 'Archived',
      children: (
        <ThemeTable
          columns={columns}
          data={data?.data?.appointments || []}
          pagination={{ total, setCurrentPage, setResultPerPage }}
          onRow={getRowProps}
          scroll={{
            x: 1200,
          }}
        />
      ),
    },
    {
      key: 'cancelled',
      label: 'Cancelled',
      children: (
        <ThemeTable
          columns={columns}
          data={data?.data?.appointments || []}
          pagination={{ total, setCurrentPage, setResultPerPage }}
          onRow={getRowProps}
          scroll={{
            x: 1200,
          }}
        />
      ),
    },
  ];

  let servicesArr =
    services?.data?.map(service => {
      return {
        value: service?.title,
        key: service?.title,
        label: service?.title,
      };
    }) || [];

  return (
    <Container
      selected={'5'}
      header={
        <div className="flex flex-col gap-4">
          <div className="flex w-full justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="flex items-center">
                <div className="bg-primary h-9 w-9 rounded-full flex justify-center items-center">
                  <img src={users_white_icon} width={17} height={17} alt="" />
                </div>
                <p className="ml-2 font-semibold text-2xl">Appointments</p>
              </div>
              <RefreshIndicator
                onManualRefresh={manualRefresh}
                paused={isSearchActive}
              />
            </div>
            <div className="flex items-center gap-4">
              <div style={{ width: 220 }}>
                <Input
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  placeholder="Search client or dog..."
                  prefix={<SearchOutlined className="text-gray-400" />}
                  allowClear
                  className="h-10"
                  disabled={isBlocking}
                />
                {searchInput.trim().length > 0 &&
                  searchInput.trim().length < 3 && (
                    <p className="text-xs text-gray-400 mt-1">
                      Type at least 3 characters…
                    </p>
                  )}
              </div>
              <ThemeButton
                content="Create Appointment"
                onClick={() => setIsCreateAppointmentModalOpen(true)}
              />
            </div>
          </div>
          <div className="flex w-full items-center">
            <Tabs
              defaultActiveKey="all"
              items={items.map(item => ({
                key: item.key,
                label: item.label,
                disabled: isBlocking,
              }))}
              onChange={onChange}
              className="flex-1 [&_.ant-tabs-nav]:mb-0"
            />
            {key === 'past' && (
              <div className="flex items-center gap-4 mr-4">
                {unpaidActive &&
                  currentData?.data?.outstandingTotal !== undefined && (
                    <p className="text-sm font-medium text-red-600">
                      Outstanding: $
                      {currentData.data.outstandingTotal.toFixed(2)}
                    </p>
                  )}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Unpaid Only</span>
                  <Switch
                    checked={unpaidOnly}
                    onChange={checked => {
                      setUnpaidOnly(checked);
                      setCurrentPage(1);
                    }}
                    disabled={isBlocking}
                    className="bg-gray-400"
                  />
                </div>
              </div>
            )}
            <ThemeDropdown
              setValue={e => {
                setServiceType(e);
                setCurrentPage(1);
              }}
              text={serviceType ? serviceType : 'All Services'}
              items={[
                { value: 'All', key: 'All', label: 'All Services' },
                ...servicesArr,
              ]}
              disabled={isBlocking}
            />
          </div>
        </div>
      }
    >
      <div
        // A user-initiated load blocks with skeleton rows; the 15s poll only
        // dims, so the table stays readable and clickable while it refreshes.
        style={{
          opacity: isStale ? 0.6 : 1,
          transition: 'opacity 150ms ease',
        }}
      >
        <ThemeTable
          columns={columns}
          data={data?.data?.appointments || []}
          loader={isBlocking}
          // Match the page size so the table doesn't jump when the real rows land.
          skeletonRows={resultPerPage}
          entityName="appointments"
          pagination={{ total, currentPage, setCurrentPage, setResultPerPage }}
          onRow={getRowProps}
          // Name the filter that emptied the table — with a tab, a service and
          // a search all narrowing at once, "No Data" leaves the admin guessing
          // which one to clear.
          emptyText={
            search
              ? `No ${unpaidActive ? 'unpaid ' : ''}appointments match "${search}".`
              : unpaidActive
                ? serviceType && serviceType !== 'All'
                  ? `No checked-out ${serviceType} appointments have an unpaid balance.`
                  : 'No checked-out appointments have an unpaid balance.'
                : serviceType && serviceType !== 'All'
                  ? `No ${key === 'all' ? '' : `${key} `}appointments for ${serviceType}.`
                  : key === 'all'
                    ? 'No appointments yet. New bookings from the app land here.'
                    : `No ${key} appointments right now.`
          }
          scroll={{
            x: 1200,
          }}
        />
      </div>

      <DogByIDModal
        isOpen={isOpen}
        onClose={onClose}
        data={dogData}
        isLoading={fetchingdog}
        onEditDog={handleEditDog}
      />
      <EditDogModal
        isOpen={isEditDogModalOpen}
        onClose={handleCloseEditDogModal}
        data={dogData}
        onSuccess={refetch}
      />
      <UserDetailModal
        isOpen={isOpenUserModal}
        onClose={onCloseUserModal}
        data={userData}
      />

      <AppointmentHistoryDrawer
        isOpen={!!historyAppointmentId}
        onClose={() => setHistoryAppointmentId(null)}
        appointmentId={historyAppointmentId}
        onOpenDog={onOpenModal}
        zIndex={1050}
      />

      <UpdateAppointmentTimeModal
        isOpen={isOpenUpdateTimeModal}
        onClose={() => {
          setIsOpenUpdateTimeModal(false);
          setAppointmentTime(null);
        }}
        isLoading={isLoading}
        onClick={onUpdateTime}
        appointmentTime={appointmentTime}
        btnText={'Modify Stay'}
        onInvoiceSent={refetch}
        zIndex={1050}
      />
      <InvoiceModal
        isOpen={isOpenInvoice}
        onClose={() => setIsOpenInvoice(false)}
      />
      <UpdateInvoiceModal
        isOpen={isOpenUpdateInvoiceModal}
        onClose={() => setIsOpenUpdateInvoiceModal(false)}
        data={invoiceData}
        setData={e => e}
        onSubmit={onUpdateInvoice}
        isLoading={isLoading}
        onInvoiceSent={refetch}
        onModify={appt => {
          setEditId(appt._id);
          setAppointmentTime(appt);
          setIsOpenUpdateTimeModal(true);
        }}
        onViewHistory={id => setHistoryAppointmentId(id)}
        onStatusChange={handleStatusChange}
        isStatusUpdating={updatingStatusIds.has(invoiceData?._id)}
      />
      <UpdateAppointment
        isOpen={isOpenNotesModal}
        onClose={() => setIsOpenNotesModal(false)}
        value={notes}
        setValue={e => setNotes(e)}
        onClick={onUpdateNotes}
        isLoading={isLoading}
        btnText={'Update Notes'}
      />
      <Modal
        title={
          pendingIsChecking ? 'Select Payment Method' : 'Edit Payment Method'
        }
        open={isPaymentMethodModalOpen}
        onOk={
          pendingIsChecking
            ? handlePaymentMethodConfirm
            : handleUpdatePaymentMethod
        }
        onCancel={() => {
          setIsPaymentMethodModalOpen(false);
          setPendingAppointmentData(null);
          setSelectedPaymentMethod(null);
          setPendingIsChecking(false);
          refetch();
        }}
        okText={pendingIsChecking ? 'Continue' : 'Update'}
        cancelText="Cancel"
        styles={{ footer: { marginTop: 24 } }}
        okButtonProps={{
          style: {
            backgroundColor: '#10b981',
            borderColor: '#10b981',
            color: '#ffffff',
          },
        }}
      >
        {pendingAppointmentData && (
          <div>
            <div
              style={{
                marginBottom: '16px',
                padding: '8px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
              }}
            >
              <p>
                <strong>Customer:</strong>{' '}
                {pendingAppointmentData?.user?.username || 'Unknown Customer'}
              </p>
              <p>
                <strong>Service:</strong>{' '}
                {pendingAppointmentData?.service?.title || 'N/A'}
              </p>
              <p>
                <strong>Amount:</strong> $
                {(
                  pendingAppointmentData?.netTotal ||
                  pendingAppointmentData?.priceAtAppoTime ||
                  0
                ).toFixed(2)}
              </p>
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                }}
              >
                Payment Method <span style={{ color: 'red' }}>*</span>
              </label>
              <ThemeSelect
                value={selectedPaymentMethod}
                onChange={setSelectedPaymentMethod}
                placeholder="Select payment method"
                options={PAYMENT_METHODS.map(method => ({
                  value: method,
                  label: method,
                }))}
                style={{ width: '100%' }}
              />
            </div>
          </div>
        )}
      </Modal>
      <CreateAppointmentModal
        isOpen={isCreateAppointmentModalOpen}
        onClose={() => setIsCreateAppointmentModalOpen(false)}
        onSuccess={() => {
          refetch();
        }}
      />
    </Container>
  );
};

export default Appointments;
